"use client";

import React, { useMemo } from 'react';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Heart, Lock, Search } from 'lucide-react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ScoreDistributionBar } from "./ScoreDistributionBar";

import { INSTITUTIONS_DATA, PROGRAMMES_DATA } from "../calculator/data";
import { getUniversityKey } from "../common";
import { ProgrammeResult } from "./ResultsTable";
import type { SortingState } from '@tanstack/react-table';

import type { ScoreTriple } from '../calculator/calculator';

import { TitleBar } from "./TitleBar";
import { CORE_SUBJECTS } from '../common';

import { YourScores } from "./YourScores";
import { ProgrammeSortControls } from "./ProgrammeSortControls";
import { UniversityFilterButtons } from "./UniversityFilterButtons";
import { ScoreQuartileFilterButtons, type ScoreQuartileRanges, type ScoreRange, type ScoreQuartileFilter } from "./ScoreQuartileFilterButtons";
import type { GatedFeature } from "./SignInRequired";

// Pre-build O(1) lookup maps (built once at module load)
const PROG_DATA_MAP = new Map<string, any>();
for (const p of PROGRAMMES_DATA) {
  PROG_DATA_MAP.set(p.jupas_id, p);
}

// --- Memoized individual card to avoid re-rendering unchanged cards ---
interface ProgrammeCardProps {
    result: ProgrammeResult;
    cardRefs: React.MutableRefObject<{ [key: string]: HTMLDivElement | null }>;
    openProgrammeDialog: (result: ProgrammeResult) => void;
    isFavourite: (programmeId: string) => boolean;
    toggleFavourite: (result: ProgrammeResult) => void;
    /** Favourites are for signed-in users only: show a padlock, not a heart. */
    favouritesLocked: boolean;
    translate: (key: any) => string;
    language: 'en' | 'zh';
    hasValidAdmissionScores: (admissionHistory: Array<{ year: string; LQ: string | number; MED: string | number; UQ: string | number; HA?: string | number }>) => boolean;
    scores: Record<string, ScoreTriple>;
    selectedElectives: string[];
}

const ProgrammeCard = React.memo<ProgrammeCardProps>(function ProgrammeCard({
    result,
    cardRefs,
    openProgrammeDialog,
    isFavourite,
    toggleFavourite,
    favouritesLocked,
    translate,
    language,
    hasValidAdmissionScores,
    scores,
    selectedElectives
}) {
    const instKey = getUniversityKey(result.jupas_id);
    const prog_data = PROG_DATA_MAP.get(result.jupas_id);
    const inst_data = instKey ? (INSTITUTIONS_DATA as any)[instKey] : null;
    const isFav = result.programmeEntry ? isFavourite(result.programmeEntry.programmeDetails.id) : false;

    return (
        <Card
            ref={(el) => { cardRefs.current[result.id] = el; }}
            className="hover:shadow-lg transition-shadow"
        >
            <div className="p-2 sm:p-3 md:p-4">
                {/* Action Buttons Row - visible on mobile at top */}
                <div className="flex justify-end items-center gap-2 md:hidden">
                    <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); openProgrammeDialog(result); }} className="cursor-pointer">
                        {translate('showDetails')}
                    </Button>
                    <Button
                        size="sm"
                        variant={isFav ? "default" : "outline"}
                        onClick={(e) => { e.stopPropagation(); toggleFavourite(result); }}
                        title={favouritesLocked ? translate('featureLoggedInOnly') : undefined}
                        className={isFav ? "bg-red-500 hover:bg-red-600 text-white prog-res-fav" : "prog-res-fav"}
                    >
                        {favouritesLocked
                            ? <Lock className="h-4 w-4 text-muted-foreground" />
                            : <Heart className={`h-4 w-4 ${isFav ? 'fill-current' : ''}`} />}
                    </Button>
                </div>

                {/* Card Header */}
                <div className="flex justify-between items-start">
                    <div className="flex-1 prog-res-title">
                        <div className="pb-3">
                            <TitleBar
                                jupasId={result.jupas_id}
                                programmeName={result.programmeName}
                                progData={prog_data}
                                instData={inst_data}
                                ger={result.ger}
                                per={result.per}
                                translate={translate}
                                userScores={scores}
                                activeSubjects={[
                                    ...CORE_SUBJECTS.map(s => s.subject),
                                    ...selectedElectives,
                                ]}
                                programmes={PROGRAMMES_DATA}
                                titleClassName="text-lg font-semibold mb-1 text-slate-900"
                                logoClassName="w-12 h-12 rounded flex-shrink-0"
                            />
                        </div>
                    </div>

                    {/* Action Buttons - hidden on mobile, shown on desktop */}
                    <div className="hidden md:flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); openProgrammeDialog(result); }} className="cursor-pointer">
                            {translate('showDetails')}
                        </Button>
                        <Button
                            size="sm"
                            variant={isFav ? "default" : "outline"}
                            onClick={(e) => { e.stopPropagation(); toggleFavourite(result); }}
                            title={favouritesLocked ? translate('featureLoggedInOnly') : undefined}
                            className={isFav ? "bg-red-500 hover:bg-red-600 text-white prog-res-fav" : "prog-res-fav"}
                        >
                            {favouritesLocked
                                ? <Lock className="h-4 w-4 text-muted-foreground" />
                                : <Heart className={`h-4 w-4 ${isFav ? 'fill-current' : ''}`} />}
                        </Button>
                    </div>
                </div>

                {/* Score Distribution Bar or Your Scores */}
                <div className="mt-0">
                    {hasValidAdmissionScores(result.admissionHistory) ? (
                        <ScoreDistributionBar
                            result={result}
                            language={language}
                            admissionHistory={result.admissionHistory.slice(0, 4)}
                            userMinScore={result.minScore}
                            userCurrentScore={result.currentScore}
                            userMaxScore={result.maxScore}
                            highestAttainableScore={result.highestAttainableScore}
                            selectedScoreType="current"
                            showPastYears={1}
                            recommendationRate={result.recommendationRate}
                            showHeader={false}
                            ger={result.ger}
                            per={result.per}
                        />
                    ) : (
                        <YourScores
                            currentScore={result.currentScore}
                            highestAttainableScore={result.highestAttainableScore}
                            translate={translate}
                        />
                    )}
                </div>
            </div>
        </Card>
    );
});

interface ResultsCardViewProps {
    isTableView: boolean;
    globalFilter: string;
    setGlobalFilter: (value: string) => void;
    universityFilter: string;
    setUniversityFilter: (value: string) => void;
    scoreQuartileRanges: ScoreQuartileRanges;
    availableQuartileRanges: Record<ScoreQuartileFilter, ScoreRange | null>;
    setScoreQuartileRanges: React.Dispatch<React.SetStateAction<ScoreQuartileRanges>>;
    isSignedIn: boolean;
    requireSignIn: (feature: GatedFeature, redirectTo?: string) => Promise<boolean>;
    sorting: SortingState;
    setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
    scrollContainerRef: React.RefObject<HTMLDivElement | null>;
    sortedResults: ProgrammeResult[];
    cardsToShow: number;
    hasMoreResultsToLoad: boolean;
    loadMoreResults: () => void;
    cardRefs: React.MutableRefObject<{ [key: string]: HTMLDivElement | null }>;
    openProgrammeDialog: (result: ProgrammeResult) => void;
    isFavourite: (programmeId: string) => boolean;
    toggleFavourite: (result: ProgrammeResult) => void;
    /** Favourites are for signed-in users only: show a padlock, not a heart. */
    favouritesLocked: boolean;
    translate: (key: any) => string;
    language: 'en' | 'zh';
    hasValidAdmissionScores: (admissionHistory: Array<{ year: string; LQ: string | number; MED: string | number; UQ: string | number; HA?: string | number }>) => boolean;
    scores: Record<string, ScoreTriple>;
    selectedElectives: string[];
}

export const ResultsCardView: React.FC<ResultsCardViewProps> = ({
    isTableView,
    globalFilter,
    setGlobalFilter,
    universityFilter,
    setUniversityFilter,
    scoreQuartileRanges,
    availableQuartileRanges,
    setScoreQuartileRanges,
    isSignedIn,
    requireSignIn,
    sorting,
    setSorting,
    scrollContainerRef,
    sortedResults,
    cardsToShow,
    hasMoreResultsToLoad,
    loadMoreResults,
    cardRefs,
    openProgrammeDialog,
    isFavourite,
    toggleFavourite,
    favouritesLocked,
    translate,
    language,
    hasValidAdmissionScores,
    scores,
    selectedElectives
}) => {
    // Visible items — sortedResults is already filtered & sorted by parent.
    // Just slice for infinite scroll.
    const visibleResults = useMemo(
        () => sortedResults.slice(0, cardsToShow),
        [sortedResults, cardsToShow]
    );

    const remaining = Math.max(sortedResults.length - visibleResults.length, 0);

    return (
        <div className={`${!isTableView ? 'flex flex-col flex-1 min-h-0' : 'hidden'}`}>
            {/* Search filter + sort buttons + institution filter */}
            <div className="mb-4 flex-shrink-0 flex flex-col gap-2">
                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                    <div className="relative w-full min-w-0 sm:w-auto sm:flex-[1_1_20rem]">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-indigo-500" />
                        <input
                            type="text"
                            value={globalFilter ?? ''}
                            onChange={e => setGlobalFilter(e.target.value)}
                            placeholder={translate('searchProgrammes') || 'Search programmes, JUPAS codes, or universities...'}
                            className="w-full rounded-lg border-2 border-indigo-400 bg-indigo-50 py-2 pl-10 pr-4 text-indigo-900 placeholder-indigo-400 focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                    </div>
                    <div className="flex min-w-0 flex-wrap items-center gap-2 sm:flex-[0_1_auto] sm:justify-end">
                        <ProgrammeSortControls sorting={sorting} setSorting={setSorting} translate={translate} />
                        <ScoreQuartileFilterButtons
                            ranges={scoreQuartileRanges}
                            availableRanges={availableQuartileRanges}
                            setRanges={setScoreQuartileRanges}
                            isSignedIn={isSignedIn}
                            requireSignIn={requireSignIn}
                            translate={translate}
                        />
                    </div>
                </div>
                <UniversityFilterButtons
                    universityFilter={universityFilter}
                    setUniversityFilter={setUniversityFilter}
                    translate={translate}
                />
            </div>
            <div
                ref={!isTableView ? scrollContainerRef : undefined}
                id={!isTableView ? "programme-results-scroll-container" : undefined}
                className="space-y-4 overflow-y-auto flex-1"
            >
                <InfiniteScroll
                    dataLength={visibleResults.length}
                    next={loadMoreResults}
                    hasMore={hasMoreResultsToLoad}
                    loader={null}
                    scrollThreshold="200px"
                    scrollableTarget="programme-results-scroll-container"
                >
                    <div className="space-y-4">
                        {visibleResults.map((result) => (
                            <ProgrammeCard
                                key={result.id}
                                result={result}
                                cardRefs={cardRefs}
                                openProgrammeDialog={openProgrammeDialog}
                                isFavourite={isFavourite}
                                toggleFavourite={toggleFavourite}
                                favouritesLocked={favouritesLocked}
                                translate={translate}
                                language={language}
                                hasValidAdmissionScores={hasValidAdmissionScores}
                                scores={scores}
                                selectedElectives={selectedElectives}
                            />
                        ))}
                    </div>
                </InfiniteScroll>
            </div>

        </div>
    );
};
